<template>
  <Sidebar
    v-model:visible="internalVisible"
    position="right"
    class="spm-table-controls-panel"
    :modal="true"
    :dismissable="true"
    :show-close-icon="false"
  >
    <div class="spm-table-controls-panel__container">
      <div class="spm-table-controls-panel__header">
        <h3 class="spm-table-controls-panel__title">
          {{ t('tableControls.title') }}
        </h3>
        <Button
          class="p-button-text p-button-rounded spm-table-controls-panel__close"
          icon="fa-regular fa-xmark"
          @click="closePanel"
        />
      </div>

      <div class="spm-table-controls-panel__content">
        <section
          v-if="sortingOptions.length"
          class="spm-table-controls-panel__section"
        >
          <h4 class="spm-table-controls-panel__section-title">
            {{ t('tableControls.sorting.title') }}
          </h4>
          <Dropdown
            v-model="localSortField"
            :options="sortingOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('tableControls.sorting.placeholder')"
            class="spm-table-controls-panel__dropdown"
            show-clear
          />
          <SelectButton
            v-if="localSortField"
            v-model="localSortOrder"
            :options="sortOrderOptions"
            option-label="label"
            option-value="value"
            class="spm-table-controls-panel__order"
          />
        </section>

        <section
          v-if="filterControls.length"
          class="spm-table-controls-panel__section"
        >
          <h4 class="spm-table-controls-panel__section-title">
            {{ t('tableControls.filters.title') }}
          </h4>
          <div
            v-for="control in filterControls"
            :key="control.key"
            class="spm-table-controls-panel__filter"
          >
            <label class="spm-table-controls-panel__filter-label">
              {{ control.label }}
            </label>
            <div class="spm-table-controls-panel__filter-body">
              <Dropdown
                v-if="matchModeOptions[control.filterKey]?.length"
                v-model="localFilters[control.filterKey].constraints[0].matchMode"
                :options="matchModeOptions[control.filterKey]"
                option-label="label"
                option-value="value"
                class="spm-table-controls-panel__match-mode"
              />
              <div class="spm-table-controls-panel__filter-input">
                <Filter
                  v-if="localFilters[control.filterKey]"
                  :col="control.column"
                  :filter-model="localFilters[control.filterKey].constraints[0]"
                  :filter-callback="noop"
                  :custom-options-items="control.column.filterSettings?.customOptionsItems"
                  :update-value="(value) => updateFilterValue(control.filterKey, value)"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="spm-table-controls-panel__footer">
        <Button
          class="p-button-secondary"
          :label="t('tableControls.reset')"
          @click="onReset"
        />
        <Button
          class="p-button-success"
          :label="t('tableControls.apply')"
          @click="onApply"
        />
      </div>
    </div>
  </Sidebar>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import { cloneDeep } from 'lodash';
import { SortOrderEnum } from '@/types/enums';
import Filter from '@/components/table/Filter.vue';
import type { ColumnFilterMatchModeOptions } from 'primevue/column';
import { SpmTableControlDescriptor, SpmTableSort } from '@/types/table-types';

interface ApplyPayload {
  filters: Record<string, any>;
  sort: SpmTableSort | null;
}

export default defineComponent({
  name: 'SpmTableControlsPanel',

  components: {
    Sidebar,
    Button,
    Dropdown,
    SelectButton,
    Filter,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    controls: {
      type: Array as PropType<SpmTableControlDescriptor[]>,
      required: true,
    },
    filters: {
      type: Object as PropType<Record<string, any>>,
      required: true,
    },
    sort: {
      type: Object as PropType<SpmTableSort | null>,
      required: false,
      default: null,
    },
  },

  emits: ['update:visible', 'apply', 'reset'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const internalVisible = ref(props.visible);
    const localFilters = reactive<Record<string, any>>({});
    const localSortField = ref<string | null>(null);
    const localSortOrder = ref<SortOrderEnum>(SortOrderEnum.ASC);

    const filterControls = computed(() => props.controls.filter((control) => control.supportsFiltering));

    const textMatchModeOptions = computed<ColumnFilterMatchModeOptions[]>(() => ([
      { label: t('filters.contains'), value: 'contains' },
      { label: t('filters.startsWith'), value: 'startsWith' },
      { label: t('filters.endsWith'), value: 'endsWith' },
      { label: t('filters.equals'), value: 'equals' },
      { label: t('filters.notEquals'), value: 'notEquals' },
      { label: t('filters.notContains'), value: 'notContains' },
      { label: t('filters.empty'), value: 'empty' },
      { label: t('filters.notEmpty'), value: 'notEmpty' },
    ]));

    const resolveFilterType = (control: SpmTableControlDescriptor): string | null => {
      const rawType = control.column.filterSettings?.type ?? control.column.type ?? null;

      if (!rawType || typeof rawType !== 'string') {
        return null;
      }

      return rawType.toLowerCase();
    };

    const isTextualFilter = (control: SpmTableControlDescriptor): boolean => {
      const type = resolveFilterType(control);

      if (!type) {
        return true;
      }

      return ![
        'dropdown',
        'select',
        'multiselect',
        'multi-select',
        'language',
        'country',
        'date',
        'datetime',
        'calendar',
      ].includes(type);
    };

    const matchModeOptions = computed<Record<string, ColumnFilterMatchModeOptions[]>>(() => {
      const map: Record<string, ColumnFilterMatchModeOptions[]> = {};

      filterControls.value.forEach((control) => {
        const explicitMatchModes = control.column.matchModes ?? [];
        const options = explicitMatchModes.length
          ? explicitMatchModes
          : (isTextualFilter(control) ? textMatchModeOptions.value : []);

        if (options.length) {
          map[control.filterKey] = options;
        }
      });

      return map;
    });

    const sortingOptions = computed(() => {
      const seen = new Set<string>();
      return props.controls
        .filter((control) => control.supportsSorting && control.sortField)
        .reduce<{ label: string; value: string; displayField?: string }[]>((acc, control) => {
          if (control.sortField && !seen.has(control.sortField)) {
            acc.push({ label: control.label, value: control.sortField });
            seen.add(control.sortField);
          }
          return acc;
        }, []);
    });

    const sortOrderOptions = computed(() => ([
      { label: t('tableControls.sorting.asc'), value: SortOrderEnum.ASC },
      { label: t('tableControls.sorting.desc'), value: SortOrderEnum.DESC },
    ]));

    const noop = () => {};

    const cloneFilterMeta = (source?: Record<string, any>) => {
      if (!source) {
        return {};
      }
      return cloneDeep(source);
    };

    const ensureLocalFilters = () => {
      const cloned = cloneFilterMeta(props.filters);

      Object.keys(localFilters).forEach((key) => {
        delete localFilters[key];
      });

      filterControls.value.forEach((control) => {
        const filterKey = control.filterKey;
        const filterMeta = cloned[filterKey];
        const availableMatchModes = matchModeOptions.value[filterKey] ?? [];
        const defaultMatchMode = control.column.filterSettings?.defaultMatchMode
          ?? control.column.matchModes?.[0]?.value
          ?? availableMatchModes[0]?.value
          ?? 'contains';

        if (filterMeta) {
          const constraints = Array.isArray(filterMeta.constraints) && filterMeta.constraints.length
            ? filterMeta.constraints
            : [{ value: '', matchMode: defaultMatchMode }];

          if (!constraints[0].matchMode || !availableMatchModes.some((mode) => mode.value === constraints[0].matchMode)) {
            constraints[0].matchMode = defaultMatchMode;
          }

          localFilters[filterKey] = {
            ...filterMeta,
            operator: filterMeta.operator ?? null,
            constraints,
          };
        } else {
          localFilters[filterKey] = {
            operator: null,
            constraints: [{
              value: '',
              matchMode: defaultMatchMode,
            }],
          };
        }
      });
    };

    const initializeState = () => {
      ensureLocalFilters();
      if (props.sort && props.sort.field) {
        localSortField.value = props.sort.field;
        localSortOrder.value = props.sort.type as SortOrderEnum;
      } else {
        localSortField.value = null;
        localSortOrder.value = SortOrderEnum.ASC;
      }
    };

    watch(() => props.visible, (value) => {
      internalVisible.value = value;
      if (value) {
        initializeState();
      }
    });

    watch(() => props.filters, () => {
      if (internalVisible.value) {
        ensureLocalFilters();
      }
    }, { deep: true });

    watch([filterControls, matchModeOptions], () => {
      if (internalVisible.value) {
        ensureLocalFilters();
      }
    });

    watch(internalVisible, (value) => {
      emit('update:visible', value);
    });

    const updateFilterValue = (key: string, value: unknown) => {
      if (!localFilters[key]) {
        return;
      }
      localFilters[key].constraints[0].value = value;
    };

    const onApply = () => {
      const payload: ApplyPayload = {
        filters: cloneFilterMeta(localFilters),
        sort: localSortField.value
          ? { field: localSortField.value, type: localSortOrder.value }
          : null,
      };

      emit('apply', payload);
      internalVisible.value = false;
    };

    const onReset = () => {
      emit('reset');
    };

    const closePanel = () => {
      internalVisible.value = false;
    };

    onMounted(() => {
      if (props.visible) {
        initializeState();
      }
    });

    return {
      t,
      internalVisible,
      localFilters,
      localSortField,
      localSortOrder,
      sortingOptions,
      filterControls,
      matchModeOptions,
      sortOrderOptions,
      updateFilterValue,
      onApply,
      onReset,
      closePanel,
      noop,
    };
  },
});
</script>

<style lang="scss">
.spm-table-controls-panel {
  width: min(720px, 95vw);
  max-width: 95vw;
  box-sizing: border-box;

  &.p-sidebar {
    width: min(720px, 95vw);
    max-width: 95vw;
  }

  .p-sidebar-content {
    padding: 0;
  }

  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  &__content {
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 1.5rem 1.5rem 1.5rem;
  }

  &__section {
    & + & {
      margin-top: 2rem;
    }
  }

  &__section-title {
    margin: 0 0 0.75rem 0;
    font-size: 1rem;
    font-weight: 600;
  }

  &__dropdown {
    width: 100%;
  }

  &__order {
    margin-top: 0.75rem;
    display: flex;
    justify-content: stretch;
    :deep(.p-selectbutton .p-button) {
      flex: 1 1 auto;
    }
  }

  &__filter {
    & + & {
      margin-top: 1.5rem;
    }
  }

  &__filter-label {
    font-weight: 500;
    margin: 0 0 0.5rem 0;
    display: block;
  }

  &__filter-body {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  &__filter-input {
    flex: 1 1 auto;

    :deep(.p-inputtext),
    :deep(.p-multiselect),
    :deep(.p-dropdown),
    :deep(.p-calendar),
    :deep(.p-autocomplete) {
      width: 100%;
    }
  }

  &__match-mode {
    min-width: 170px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
 }
</style>
